import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, Mail } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, formatFullDate } from "@/lib/format";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function NewsletterDialog({ children }) {
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Angemeldet.");
      setEmail(""); setOpen(false);
    } catch { toast.error("Anmeldung fehlgeschlagen."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-offwhite border-edborder">
        <DialogHeader>
          <DialogTitle className="font-headline text-2xl text-prussian">KONTINENTZEIT Newsletter</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Die wichtigsten Nachrichten aus Deutschland und Europa – jeden Morgen in Ihrem Postfach.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3 mt-2">
          <input
            data-testid="newsletter-email-input"
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Ihre E-Mail-Adresse"
            className="border border-edborder bg-white px-4 py-3 text-sm outline-none focus:border-prussian"
          />
          <button data-testid="newsletter-submit-btn" type="submit"
            className="bg-prussian text-white font-body font-semibold text-sm uppercase tracking-wide py-3 hover:bg-prussian-dark transition-colors">
            Kostenlos abonnieren
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/suche?q=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setMobileOpen(false); }
  };

  const [activeLang, setActiveLang] = useState(() =>
    (typeof document !== "undefined" && /googtrans=(?:\/|%2f)[a-z-]+(?:\/|%2f)en\b/i.test(document.cookie)) ? "en" : "de"
  );

  // Drive Google Translate's internal combo directly (reliable both ways, no reload).
  const applyCombo = (target, attempt = 0) => {
    const combo = document.querySelector("select.goog-te-combo");
    if (combo) {
      combo.value = target; // 'en' translates to English, 'de' restores the original
      combo.dispatchEvent(new Event("change"));
    } else if (attempt < 40) {
      setTimeout(() => applyCombo(target, attempt + 1), 250);
    }
  };

  const setLanguage = (target) => {
    if (target === activeLang) return;
    const host = window.location.hostname;
    const exp = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    if (target === "en") {
      document.cookie = "googtrans=/de/en;path=/";
      document.cookie = `googtrans=/de/en;path=/;domain=.${host}`;
    } else {
      document.cookie = `googtrans=;path=/;${exp}`;
      document.cookie = `googtrans=;path=/;domain=.${host};${exp}`;
    }
    setActiveLang(target);
    applyCombo(target);
  };

  // Re-apply English after a hard reload if the cookie says so.
  useEffect(() => {
    if (activeLang === "en") applyCombo("en");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-offwhite shadow-[0_1px_0_rgba(10,42,78,0.12)]">
      {/* Thin date bar */}
      <div className="bg-prussian">
        <div className="max-w-[1240px] mx-auto px-4 flex items-center justify-between h-6 text-[10px] font-body text-white/75 uppercase tracking-wide">
          <span data-testid="header-date">{formatFullDate()}</span>
          <Link to="/archiv" className="hover:text-gold transition-colors">Archiv</Link>
        </div>
      </div>

      {/* Masthead – light background */}
      <div className="bg-offwhite">
        <div className="max-w-[1240px] mx-auto px-4">
          <div className="relative flex items-center justify-center py-3 md:py-4">
            <button className="lg:hidden absolute left-0 text-prussian" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle" aria-label="Menü">
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <Link to="/" data-testid="brand-logo-kontinentzeit" aria-label="KONTINENTZEIT" className="block notranslate">
              <img src="/kontinentzeit-logo.webp" alt="KONTINENTZEIT – Deutschland & Europa"
                className="h-16 md:h-24 w-auto object-contain" />
            </Link>

            <div className="absolute right-0 flex items-center gap-2 sm:gap-3">
              <div data-testid="lang-switcher" className="flex items-center border border-prussian/40 divide-x divide-prussian/30 notranslate">
                <button data-testid="lang-de-btn" onClick={() => setLanguage("de")} title="Auf Deutsch anzeigen"
                  className={`px-2.5 py-1 text-[11px] font-body font-bold uppercase tracking-wide transition-colors ${activeLang === "de" ? "bg-prussian text-white" : "text-prussian hover:bg-prussian/10"}`}>
                  DE
                </button>
                <button data-testid="lang-en-btn" onClick={() => setLanguage("en")} title="Show in English"
                  className={`px-2.5 py-1 text-[11px] font-body font-bold uppercase tracking-wide transition-colors ${activeLang === "en" ? "bg-prussian text-white" : "text-prussian hover:bg-prussian/10"}`}>
                  EN
                </button>
              </div>
              <button data-testid="header-search-button" onClick={() => setSearchOpen(!searchOpen)} className="text-prussian hover:text-gold transition-colors" aria-label="Suche">
                <Search size={20} />
              </button>
              <NewsletterDialog>
                <button data-testid="header-newsletter-button"
                  className="hidden sm:inline-flex items-center gap-1.5 border border-prussian text-prussian font-body font-semibold text-xs uppercase tracking-wide px-3.5 py-2 hover:bg-prussian hover:text-white transition-colors">
                  <Mail size={14} /> Newsletter
                </button>
              </NewsletterDialog>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation bar */}
      <nav data-testid="main-navigation-menu" className="border-y border-prussian/25 bg-offwhite">
        <div className="max-w-[1240px] mx-auto px-4">
          <ul className="hidden lg:flex items-center justify-center gap-9 h-11">
            {CATEGORIES.map((c) => (
              <li key={c}>
                <NavLink to={`/rubrik/${c}`} data-testid={`nav-${c}`}
                  className={({ isActive }) =>
                    `font-body text-[13px] font-medium uppercase tracking-[0.14em] transition-colors ${isActive ? "text-gold-hover" : "text-prussian hover:text-gold-hover"}`}>
                  {CATEGORY_LABELS[c]}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {searchOpen && (
        <div className="border-b border-edborder bg-white">
          <form onSubmit={doSearch} className="max-w-[1240px] mx-auto px-4 py-3 flex gap-2">
            <input autoFocus data-testid="header-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Suche nach Artikeln, Themen, Autoren…"
              className="flex-1 border border-edborder bg-offwhite px-4 py-2.5 text-sm outline-none focus:border-prussian" />
            <button className="bg-prussian text-white font-semibold text-sm px-5 uppercase tracking-wide hover:bg-prussian-dark transition-colors">Suchen</button>
          </form>
        </div>
      )}

      {mobileOpen && (
        <nav className="lg:hidden border-b border-edborder bg-white">
          {CATEGORIES.map((c) => (
            <NavLink key={c} to={`/rubrik/${c}`} onClick={() => setMobileOpen(false)}
              className="block px-4 py-3 text-prussian font-body font-medium uppercase text-sm tracking-wide border-b border-edborder hover:text-gold-hover">
              {CATEGORY_LABELS[c]}
            </NavLink>
          ))}
          <Link to="/archiv" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-muted-foreground font-body uppercase text-sm">Archiv</Link>
        </nav>
      )}
    </header>
  );
}
